// src/utils/InventoryFilters.js
import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  Chip,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { faFilter, faUndo } from "@fortawesome/free-solid-svg-icons";
import ActionButton from "../components/ui/ActionButton";

export default function InventoryFilters({
  darkMode,
  t,

  // date range
  draftDateFrom,
  setDraftDateFrom,
  draftDateTo,
  setDraftDateTo,

  // period
  draftPeriod,
  setDraftPeriod,

  // materials
  draftSelectedMaterials,
  setDraftSelectedMaterials,
  materialOptions,

  // actions
  isModified,
  refreshing,
  onReset,
  onApply,
}) {
  const menuProps = useMemo(
    () => ({
      disablePortal: false,
      anchorOrigin: { vertical: "bottom", horizontal: "left" },
      transformOrigin: { vertical: "top", horizontal: "left" },
    }),
    []
  );

  return (
    <div className="rx-inv-filters-wrapper">
      <div className={`rx-inv-filters ${darkMode ? "rx-inv-filters--dark" : "rx-inv-filters--light"}`}>
        {/* Date Range */}
        <Box className="rx-filter-group">
          <Typography variant="caption" className="rx-filter-label">
            {t("Date Range")}
          </Typography>
          <Stack direction="row" spacing={1}>
            <TextField
              type="date"
              size="small"
              value={draftDateFrom}
              onChange={(e) => setDraftDateFrom(e.target.value)}
              className="rx-filter-input date-input"
              inputProps={{ style: { color: darkMode ? "#fff" : "#1a1a1a" } }}
            />
            <TextField
              type="date"
              size="small"
              value={draftDateTo}
              onChange={(e) => setDraftDateTo(e.target.value)}
              className="rx-filter-input date-input"
              inputProps={{ style: { color: darkMode ? "#fff" : "#1a1a1a" } }}
            />
          </Stack>
        </Box>

        {/* Period */}
        <Box className="rx-filter-group">
          <Typography variant="caption" className="rx-filter-label">
            {t("Period")}
          </Typography>
          <Select
            size="small"
            value={draftPeriod}
            onChange={(e) => setDraftPeriod(e.target.value)}
            className="rx-filter-input period-select"
            MenuProps={menuProps}
            sx={{ "& .MuiSelect-select": { color: darkMode ? "#fff" : "#1a1a1a" } }}
          >
            <MenuItem value="D">{t("Daily")}</MenuItem>
            <MenuItem value="W">{t("Weekly")}</MenuItem>
            <MenuItem value="M">{t("Monthly")}</MenuItem>
            <MenuItem value="Y">{t("Yearly")}</MenuItem>
          </Select>
        </Box>

        {/* Material Type */}
        <Box className="rx-filter-group rx-filter-group-grow" sx={{ flex: 1 }}>
          <Typography variant="caption" className="rx-filter-label">
            {t("Material Type")}
          </Typography>
          <Select
            multiple
            size="small"
            value={draftSelectedMaterials}
            onChange={(e) => setDraftSelectedMaterials(e.target.value)}
            input={<OutlinedInput />}
            displayEmpty
            renderValue={(selected) =>
              selected.length === 0 ? (
                <span className="rx-filter-placeholder">{t("All Materials")}</span>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((s) => (
                    <Chip key={String(s)} label={String(s)} size="small" />
                  ))}
                </Box>
              )
            }
            className="rx-filter-input material-select"
            MenuProps={menuProps}
            sx={{
              width: "100%",
              "& .MuiSelect-select": { color: darkMode ? "#fff" : "#1a1a1a" },
            }}
          >
            {materialOptions.map((mat) => (
              <MenuItem key={mat} value={mat}>
                {mat}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Reset */}
        {isModified && (
          <ActionButton
            variant="default"
            icon={faUndo}
            onClick={onReset}
            disabled={refreshing}
          >
            {t("Reset")}
          </ActionButton>
        )}

        {/* Apply */}
        <ActionButton
          variant="secondary"
          icon={faFilter}
          onClick={onApply}
          disabled={refreshing}
          spinning={refreshing}
        >
          {t("Apply")}
        </ActionButton>
      </div>
    </div>
  );
}
